/**
 * Types for User Stories and Backlog Management
 */

/**
 * Standard task that should be included in every user story
 */
export interface StandardTask {
  title: string;
  description: string;
  type: 'Contextualización' | 'Desarrollo' | 'Pruebas' | 'Despliegue';
  estimated_hours: number;
}

/**
 * User Story with all required fields
 */
export interface UserStory {
  id: string;
  title: string;
  user_story: string;  // "Como [rol], quiero [acción], para [beneficio]"
  rol: string;
  criterios_funcionales: string[];
  criterios_tecnicos: string[];
  figma_links: string[];
  insumos: string[];
  story_points: number;
  tasks: StandardTask[];
  priority: PriorityLevel;
  priority_sequence?: number | null;
  epic?: string;
  status: 'draft' | 'review' | 'approved';
}

/**
 * Epic containing multiple user stories
 */
export interface Epic {
  id: string;
  title: string;
  description: string;
  stories: UserStory[];
}

/**
 * Complete backlog structure
 */
export interface ParsedBacklog {
  executive_summary: string;
  business_rules?: string[];
  epics: Epic[];
  stories: UserStory[];
  raw_markdown: string;
}

/**
 * Validation error for a story field
 */
export interface FieldError {
  field: keyof UserStory;
  message: string;
}

/**
 * Story validation result
 */
export interface StoryValidation {
  isValid: boolean;
  errors: FieldError[];
}

/**
 * Roles que reciben tareas estándar de desarrollo
 * Solo Backend, Frontend e Integración tienen tareas estándar definidas
 */
export const DEVELOPMENT_ROLES = ['back', 'front', 'frontend', 'backend', 'integracion', 'integración', 'desarrollador backend', 'desarrollador frontend'];

/**
 * Default standard tasks as per Bancolombia specifications
 * Solo aplican para roles de desarrollo (Backend, Frontend, Integración)
 */
export const STANDARD_TASKS: StandardTask[] = [
  {
    title: "Contextualización/entendimiento",
    description: "Revisar documentación técnica, analizar diseños en Figma, entender los requerimientos de negocio y definir el alcance técnico detallado de la historia.",
    type: "Contextualización",
    estimated_hours: 2
  },
  {
    title: "Desarrollo",
    description: "Implementar la funcionalidad siguiendo los estándares de código, patrones de arquitectura y buenas prácticas definidas por el equipo.",
    type: "Desarrollo",
    estimated_hours: 8
  },
  {
    title: "Pruebas unitarias",
    description: "Ejecutar pruebas unitarias, de integración y funcionales. Validar criterios de aceptación y corregir defectos encontrados.",
    type: "Pruebas",
    estimated_hours: 4
  },
  {
    title: "Despliegue en ambientes preproductivos",
    description: "Preparar el código para despliegue, documentar cambios, coordinar con DevOps y validar funcionamiento en ambiente de staging.",
    type: "Despliegue",
    estimated_hours: 2
  }
];

/**
 * Verificar si un rol debe tener tareas estándar
 */
export const shouldHaveStandardTasks = (rol: string): boolean => {
  const rolLower = rol.toLowerCase();
  return DEVELOPMENT_ROLES.some(devRole => 
    rolLower.includes(devRole) || 
    devRole.includes(rolLower) ||
    rolLower.includes('[back]') ||
    rolLower.includes('[front]') ||
    rolLower.includes('[integraci')
  );
};

export type StoryStack = 'front' | 'back' | 'integracion' | null;

export const getStoryStackFromText = (text: string): StoryStack => {
  const textLower = text.toLowerCase();
  if (textLower.includes('[front]') || textLower.includes('frontend')) {
    return 'front';
  }
  if (textLower.includes('[back]') || textLower.includes('backend')) {
    return 'back';
  }
  if (
    textLower.includes('[integración]') ||
    textLower.includes('[integracion]') ||
    textLower.includes('[integration]') ||
    textLower.includes('integración') ||
    textLower.includes('integracion')
  ) {
    return 'integracion';
  }
  return null;
};

const TECHNICAL_CRITERIA_FRONT_RAW = `
Cr1 : La maquetación esta acorde al figma y la necesidad
* Comportamiento visual de elementos acorde a lo pedido

Cr2 Pruebas Unitarias de maquetación
* Las necesarias a criterio del Ingeniero de SW

Cr3 Front: Crear HA Pruebas E2E
* Aplica si se modifican pantallas existentes en las E2E

Cr4 Front: Integrar desarrollo correctamente a la rama current
* De acuerdo a los impactos se analiza si pude ir a trunk

Cr5 Front: Crear HA para Paso a Producción
* Se crea dependiendo de la completitud de todo lo anterior
* Se crea dependiendo de que afecte o no algo existente
* Creación de DOD
* Checklist Pruebas de Performance (Siempre escoger Look and Feel ya que estamos excepcionados para Front)

Cr6: Documentación técnica en README

Cr7: QA Visual
* Componente que no quede como el figma, deben negociarse con Diseño de experiencia

Cr8: Validar si el componente se impacta a nivel transversal (desde la librería), en ese caso avisar a través del chat LC Evolución con el fin de evaluar las las posibles afectaciones que pueda tener y contar con las aprobaciones de cada célula
`;

const TECHNICAL_CRITERIA_BACK_RAW = `
Cr1 : Pruebas Unitarias 80% y deuda técnica minima posible (Nuevo código)
* Mutation Test debe conservarse por encima de 50%

Cr2: Crear, reusar o modificar servicios para cumplir con lo especificado en la maquetación (CRUD)

Cr3: Si no se tiene maquetacion, definir datos entrada, proceso y salida.

Cr4: Implementar pruebas de aceptación del servicio
* Realizar prueba de respuesta exitosa
* Realizar prueba(s) para escenarios de falla
* Para todos los endpoints

Cr5: Integrar desarrollo correctamente a la rama current
* De acuerdo a los impactos se analiza si pude ir a trunk

Cr6: Diligenciar la informacion del endpoint creado en el archivo de inventario de servicios

Cr7: Crear HA para Pruebas de Performance
* Linea base
* Carga
* Estrés

Cr8: Crear HA para Paso a Producción
* Se crea dependiendo de la completitud de todo lo anterior
* Se crea dependiendo de que afecte o no algo existente
* Creación de DOD
* Checklist Pruebas de Performance
`;

const TECHNICAL_CRITERIA_INTEGRACION_RAW = `
Cr1: Integración del desarrollo

Cr2 : Comportamiento de la pantalla de acuerdo a lo solicitado por el usuario
* Se puede referenciar la HU de maquetación

Cr3 : Pruebas Unitarias Lógica

Cr4 : Crear HA Pruebas E2E
* Aplica si la funcionalidad es crítica bajo criterio de P.O.

Cr5: Crear HA para Paso a Producción
* Depende de la completitud de todo lo anterior
* Dependiendo de que afecte o no algo existente
* Creación de DOD
* Checklist Pruebas de Performance
`;

const normalizeDefaultTechnicalCriteria = (raw: string): string[] => {
  return raw
    .split(/\n\s*\n/g)
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => item.replace(/\n+/g, ' | '));
};

const TECHNICAL_CRITERIA_FRONT_LIST = normalizeDefaultTechnicalCriteria(TECHNICAL_CRITERIA_FRONT_RAW);
const TECHNICAL_CRITERIA_BACK_LIST = normalizeDefaultTechnicalCriteria(TECHNICAL_CRITERIA_BACK_RAW);
const TECHNICAL_CRITERIA_INTEGRACION_LIST = normalizeDefaultTechnicalCriteria(TECHNICAL_CRITERIA_INTEGRACION_RAW);

export const getDefaultTechnicalCriteriaByStack = (stack: StoryStack): string[] => {
  if (stack === 'front') {
    return [...TECHNICAL_CRITERIA_FRONT_LIST];
  }
  if (stack === 'integracion') {
    return [...TECHNICAL_CRITERIA_INTEGRACION_LIST];
  }
  if (stack === 'back') {
    return [...TECHNICAL_CRITERIA_BACK_LIST];
  }
  return [];
};

export const getDefaultTechnicalCriteriaForStory = (title: string, rol: string): string[] => {
  const stackFromTitle = getStoryStackFromText(title);
  if (stackFromTitle) {
    return getDefaultTechnicalCriteriaByStack(stackFromTitle);
  }
  const stackFromRole = getStoryStackFromText(rol);
  return getDefaultTechnicalCriteriaByStack(stackFromRole);
};

/**
 * Fibonacci sequence for story points estimation
 */
export const STORY_POINTS_OPTIONS = [1, 2, 3, 5, 8, 13];

/**
 * Priority options
 */
export type PriorityLevel = 'Alta' | 'Media' | 'Baja';

export const PRIORITY_OPTIONS: PriorityLevel[] = ['Alta', 'Media', 'Baja'];

export const normalizePrioritySequence = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.round(parsed);
  if (rounded < 1) return null;
  return rounded;
};

export const parsePriorityAndSequence = (rawValue: string): {
  priority: PriorityLevel;
  priority_sequence: number | null;
} => {
  const text = String(rawValue || '').trim();
  const lower = text.toLowerCase();

  let priority: PriorityLevel = 'Media';
  if (lower.includes('alta') || lower.includes('high') || lower.includes('crítica') || lower.includes('critica')) {
    priority = 'Alta';
  } else if (lower.includes('baja') || lower.includes('low')) {
    priority = 'Baja';
  }

  const sequenceCandidate =
    text.match(/#\s*p?-?\s*(\d{1,6})/i)?.[1] ||
    text.match(/\bp-?(\d{1,6})\b/i)?.[1] ||
    text.match(/\((\d{1,6})\)\s*$/)?.[1] ||
    text.match(/\bsecuencia\s*:?\s*(\d{1,6})\b/i)?.[1] ||
    text.match(/^\s*(\d{1,6})\s*$/)?.[1];

  return {
    priority,
    priority_sequence: normalizePrioritySequence(sequenceCandidate ?? null),
  };
};

export const formatPriorityTag = (prioritySequence?: number | null): string | null => {
  const normalized = normalizePrioritySequence(prioritySequence);
  if (!normalized) return null;
  return `#P${String(normalized).padStart(3, '0')}`;
};

/**
 * Story status options
 */
export const STATUS_OPTIONS: Array<'draft' | 'review' | 'approved'> = ['draft', 'review', 'approved'];
